import BreadCrumb from "components/lib/Global/BreadCrumb";
import Tabs from "components/lib/Shared/Tabs";
import { Constants } from "config/constants";
import { ManageModuleContext } from "context/ManageCompanyModuleContext";
import ManualTransactionEditContext from "context/ManualTransactionEditContext";
import ManualTransactionReviewContext from "context/ManualTransactionReviewContext";
import UpdateCompanyDetailsContext from "context/UpdateCompanyDetails";
import { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  useLocation,
  useParams,
} from "react-router-dom/cjs/react-router-dom.min";
import {
  createBatchNumber,
  resetBatchNumber,
  setBatchNumber,
} from "state/slices/batchNumber";
import {
  getAllInitiateTransactionChangeAsync,
  resetTransactionChange,
} from "state/slices/initiateTransactionChange";
import { handleCompanyMaintenanceFormData } from "utils/transactionRequestHandler";
import ApplicantDetails from "./ApplicantDetails";
import Approvals from "./Approvals";
import BeneficialOwners from "./BeneficialOwners";
import BRSForms from "./BRSForms";
import ChangeType from "./ChangeType";
import RegisteredAddress from "./RegisteredAddress";
import ShareInformation from "./ShareInformation";
import StakeholderDetails from "./StakeholderDetails";
import Status from "./Status";
import Summary from "./Summary";

const UpdateCompanyDetails = () => {
  let initialTabs = [
    {
      id: "applicantDetails",
      label: "Applicant Details",
    },
    {
      id: "changeType",
      label: "Change Type",
    },
    {
      id: "summary",
      label: "Summary",
    },
    {
      id: "registeredAddress",
      label: "Registered Address",
    },
    {
      id: "shareInformation",
      label: "Share Information",
    },
    {
      id: "stakeholderDetails",
      label: "Stakeholder Details",
    },
    {
      id: "beneficialOwners",
      label: "Beneficial Owners",
    },
    {
      id: "approvals",
      label: "Approvals",
    },
    {
      id: "brsForms",
      label: "BRS Forms",
    },
    {
      id: "status",
      label: "Status",
    },
  ];

  const { batchNo } = useSelector((state) => state.batchNumberSlice);
  const { company } = useSelector((state) => state.companyDashboardSlice);

  const { isInManualTransactionReviewContext } = useContext(
    ManualTransactionReviewContext
  );
  const {
    transactionToEdit,
    setRefechTransactionOnEditContext,
    isInManualTransactionEditContext,
    setTransactionToEdit,
  } = useContext(ManualTransactionEditContext);
  const { companyId } = useParams();
  const location = useLocation();
  const dispatch = useDispatch();

  const manualTransactionReviewTabs = initialTabs;
  const [tabs, setTabs] = useState(
    isInManualTransactionReviewContext
      ? manualTransactionReviewTabs
      : initialTabs.slice(0, 2)
  );
  const [checkedTabs, setCheckedTabs] = useState([]);
  const [selectionsMade, setSelectionsMade] = useState({});
  const [selectedTab, setSelectedTab] = useState(tabs[0]);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [summarySubTabIndex, setSummarySubTabIndex] = useState(0);
  const [isStrikeOffSelected, setIsStrikeOffSelected] = useState(false);
  const [isConversionSelected, setIsConversionSelected] = useState(false);
  const [agreeToSubmit, setAgreeToSubmit] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [disableSubmitBtn, setDisableSubmitBtn] = useState(true);
  const [createdTransactionsIds, setCreatedTransactionsIds] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [refechTransactionsOnUpdate, setRefechTransactionsOnUpdate] =
    useState(false);
  const [selectionErrors, setSelectionErrors] = useState([]);

  //FileName of template articles to be deleted from aws for gornance summary
  const [fileNameToDelete, setFileNameToDelete] = useState("");

  const { comments } = useSelector((state) => state.commentsSlice);
  let unresolvedComments = comments.filter((comment) => !comment.isResolved);
  const submitToLabel =
    unresolvedComments.length !== 0 ? "applicant" : "Next Step";

  // Parse URL query parameters to get the desired tab
  const queryParams = new URLSearchParams(location.search);
  const desiredTab = queryParams.get("tab");

  const noApprovalSelections = useMemo(() => {
    const allSelections = [];

    // Add specific selections from selectionsMade
    if (selectionsMade?.summary?.companyChanges) {
      allSelections.push(...selectionsMade.summary.companyChanges);
    }
    if (selectionsMade?.summary?.securities) {
      allSelections.push(...selectionsMade.summary.securities);
    }
    if (selectionsMade?.summary?.annualCompliance) {
      allSelections.push(...selectionsMade.summary.annualCompliance);
    }

    if (selectionsMade?.stakeholderDetails?.changeOfParticulars) {
      allSelections.push(
        ...selectionsMade.stakeholderDetails.changeOfParticulars
      );
    }

    return allSelections;
  }, [selectionsMade]);

  const includeApprovalTabs = () => {
    if (noApprovalSelections.length === 1) {
      return ![
        "Administrator",
        "annualReturns",
        "debentureAndChargesDischarge",
        "noticeToCancelConversionPVTtoPLC",
        "noticeToCancelConversionPLCtoPVT",
        ,
      ].includes(noApprovalSelections[0]);
    } else if (noApprovalSelections.length > 1) {
      const comparisonArray = [
        "Administrator",
        "annualReturns",
        "debentureAndChargesDischarge",
        "noticeToCancelConversionPVTtoPLC",
        "noticeToCancelConversionPLCtoPVT",
        ,
      ];
      return !noApprovalSelections.every((item) =>
        comparisonArray.includes(item)
      );
    } else {
      return true;
    }
  };

  // Callback to update parent tabs based on checkedTabs from ChangeType
  const updateParentTabs = () => {
    let newTabs = [
      ...initialTabs.slice(0, 2), // Keep applicantDetails and changeType
      ...initialTabs.filter((tab) => checkedTabs?.includes(tab?.id)), // Add checked tabs
    ];

    const shareInformation = selectionsMade?.shareInformation;

    const shareInfoToIncludeStakeholders = () => {
      if (shareInformation) {
        let capitalIncrease = shareInformation?.capitalIncrease;
        let capitalReduction = shareInformation?.capitalReduction;
        let sharesManagement = shareInformation?.sharesManagement;
        let shareholdingChanges = shareInformation?.shareholdingChanges;
        return (
          capitalIncrease?.includes("privateWithMoreThanOneClass") ||
          capitalIncrease?.includes("PVTwithOneClass") ||
          capitalIncrease?.includes("existingAuth") ||
          capitalIncrease?.includes("unallotedShares") ||
          capitalReduction?.length > 0 ||
          sharesManagement?.includes("sharesConsolidation") ||
          sharesManagement?.includes("sharesSubDivision") ||
          sharesManagement?.includes("sharesSubdivision") ||
          shareholdingChanges?.length > 0
        );
      }
    };

    const hasReorgShareManagement = (() => {
      if (!shareInformation) return false;
      const sharesManagement = shareInformation?.sharesManagement;
      return (
        sharesManagement?.includes("sharesConsolidation") ||
        sharesManagement?.includes("sharesSubDivision") ||
        sharesManagement?.includes("sharesSubdivision")
      );
    })();

    if (
      shareInfoToIncludeStakeholders() &&
      !newTabs.includes({
        id: "stakeholderDetails",
        label: "Stakeholder Details",
      })
    ) {
      newTabs = [
        ...newTabs,
        ...initialTabs.filter((tab) => tab?.id === "stakeholderDetails"),
      ];
    }

    if (
      shareInfoToIncludeStakeholders() &&
      !hasReorgShareManagement &&
      !newTabs.includes({
        id: "beneficialOwners",
        label: "Beneficial Owners",
      })
    ) {
      newTabs = [
        ...newTabs,
        ...initialTabs.filter((tab) => tab?.id === "beneficialOwners"),
      ];
    }

    // Only include default tabs (approvals, brsForms, status) if checkedTabs is non-empty
    if (checkedTabs.length > 0) {
      newTabs = [...newTabs, ...initialTabs.slice(7, 10)];
    }

    if (!includeApprovalTabs()) {
      newTabs = [...newTabs.filter((tab) => tab?.id !== "approvals")];
    }

    let filteredTabs = [];
    newTabs.forEach((tab) => {
      if (!filteredTabs.includes(tab)) {
        filteredTabs.push(tab);
      }
    });

    setTabs(filteredTabs);

    // Set the selected tab to the first checked tab (after applicantDetails and changeType)

    setSelectedTabIndex(2);
    setSelectedTab(filteredTabs[2] || tabs[2]);
    setSummarySubTabIndex(0);
  };

  // Callback to remove a specific tab from parent tabs when deselected in ChangeType
  const removeParentTab = (tabId) => {
    let newTabs = tabs.filter((tab) => tab.id !== tabId);

    // If checkedTabs is empty, ensure default tabs are removed
    if (checkedTabs.length === 1 && checkedTabs.includes(tabId)) {
      newTabs = initialTabs.slice(0, 2); // Reset to only applicantDetails and changeType
    }

    setTabs(newTabs);
    if (selectedTab.id === tabId) {
      const newIndex = Math.min(selectedTabIndex, newTabs.length - 1);
      setSelectedTabIndex(newIndex);
      setSelectedTab(newTabs[newIndex] || newTabs[0]);
      setSummarySubTabIndex(0);
    }
  };

  //function to handle selections when on edit mode
  const getAllSelectionsArray = (selectionsMade) => {
    if (!selectionsMade || typeof selectionsMade !== "object") return [];
    return Object.values(selectionsMade)
      .flatMap((section) =>
        section && typeof section === "object"
          ? Object.values(section).flat()
          : []
      )
      .filter(Boolean);
  };

  let selections = useMemo(() => {
    return getAllSelectionsArray(selectionsMade);
  }, [selectionsMade]);

  if (transactionToEdit) {
    useEffect(() => {
      if (noApprovalSelections?.length > 0 && selections?.length === 1) {
        updateParentTabs();
      }
    }, [noApprovalSelections, selections]);
  }

  useEffect(() => {
    if (desiredTab) {
      const tabIndex = tabs.findIndex((tab) => tab.id === desiredTab);

      if (tabIndex !== -1) {
        setSelectedTabIndex(tabIndex);
      }
    }
  }, [desiredTab, noApprovalSelections, tabs]);

  useEffect(() => {}, [queryParams]);

  useEffect(() => {
    setSelectedTab(tabs[selectedTabIndex]);
  }, [selectedTabIndex, tabs]);

  const handleBackToChangesDashboard = () => {
    setshowRecentChanges(false);
    setselectedProcess("");
  };

  transactionEditTabs(
    transactionToEdit,
    setCheckedTabs,
    checkedTabs,
    setSelectionsMade,
    setTabs,
    setSelectedTabIndex,
    setIsStrikeOffSelected,
    setIsConversionSelected,
    company
  );

  const { handleBackToManageYourCompanyDashboard } =
    useContext(ManageModuleContext);

  const handleGoBack = () => {
    setSelectedTabIndex(selectedTabIndex - 1);
    setSummarySubTabIndex(0);
  };

  const handleGoForward = () => {
    setSelectedTabIndex(selectedTabIndex + 1);
    setSummarySubTabIndex(0);
  };

  const pages = [
    { name: "Home", href: "#", current: false },
    {
      name: "Maintain a company",
      current: false,
      href: "#",
      handleClick: handleBackToManageYourCompanyDashboard,
    },
    {
      name: "Update Company Details",
      current: true,
      href: null,
      handleClick: handleBackToChangesDashboard,
    },
  ];

  const isInUpdateCompanyDetailsContext = true;
  createdTransactionsIds;
  //*! Utility Functions for createdTransactionsIds and transactionEdit Operations
  // 1. Add Unique Transaction ID
  const addUniqueTransactionId = (id) => {
    if (id && !createdTransactionsIds.includes(id)) {
      setCreatedTransactionsIds((prev) => [...prev, id]);
    }
  };
  // 2. Remove Transaction ID
  const removeTransactionId = (id) => {
    setCreatedTransactionsIds((prev) =>
      prev.filter((existingId) => existingId !== id)
    );
  };
  // 3. Function to check if a transaction already exists
  const transactionExists = (changeType) => {
    for (const transaction of transactions) {
      if (transaction.changeType === changeType) {
        return transaction;
      }
    }
    return false;
  };

  useEffect(() => {
    if (transactionToEdit?._id) {
      addUniqueTransactionId(transactionToEdit._id);
    }
  }, [transactionToEdit?._id, createdTransactionsIds]);

  useEffect(() => {
    if (transactionToEdit?._id) {
      const linkedChildTransactions =
        transactionToEdit?.linkedChildTransactions;
      let trxIds = [...createdTransactionsIds, transactionToEdit?._id];
      if (linkedChildTransactions?.length > 0) {
        trxIds = [...trxIds, ...linkedChildTransactions];
      }
      setCreatedTransactionsIds(trxIds);
    }
  }, [transactionToEdit?._id]);

  useEffect(() => {
    if (transactionToEdit?._id) {
      const linkedChildTransactions =
        transactionToEdit?.linkedChildTransactions;
      let trxIds = [...createdTransactionsIds, transactionToEdit?._id];
      if (linkedChildTransactions?.length > 0) {
        trxIds = [...trxIds, ...linkedChildTransactions];
      }
      setCreatedTransactionsIds(trxIds);
    }
  }, [transactionToEdit?._id]);

  useEffect(() => {
    if (transactionToEdit?.batchNo) {
      dispatch(setBatchNumber(transactionToEdit?.batchNo));
    } else {
      if (createdTransactionsIds.length > 1 && !batchNo) {
        dispatch(createBatchNumber());
      } else if (createdTransactionsIds.length === 1 && batchNo) {
        dispatch(resetBatchNumber());
      }
    }
  }, [createdTransactionsIds, transactionToEdit?._id]);

  useEffect(() => {
    createdTransactionsIds.forEach(async (trxId) => {
      const result = await handleCompanyMaintenanceFormData(
        "existing-request",
        {
          documentId: trxId,
          batchNo,
        },
        companyId
      );
    });
  }, [batchNo, createdTransactionsIds]);

  const fetchTransactions = async () => {
    if (createdTransactionsIds.length > 0) {
      const result = await dispatch(
        getAllInitiateTransactionChangeAsync({
          companyId,
          params: {
            transactionType: Constants.TRANSACTION_TYPES.MANUAL,
            transactionIds: createdTransactionsIds.filter((id) => Boolean(id)),
            batchNo: batchNo ?? undefined,
          },
        })
      );

      ///on refech transactions on update
      if (refechTransactionsOnUpdate && isInManualTransactionEditContext) {
        setRefechTransactionOnEditContext(true);
      }

      if (result?.payload?.items?.length > 0) {
        setTransactions(result.payload.items);
      }
      // Reset the refetch flag so subsequent calls can toggle it again.
      if (refechTransactionsOnUpdate) {
        setRefechTransactionsOnUpdate(false);
      }
    } else {
      dispatch(resetTransactionChange());
    }
  };

  useEffect(async () => {
    await fetchTransactions();
  }, [createdTransactionsIds, selectedTabIndex, refechTransactionsOnUpdate]);

  return (
    <UpdateCompanyDetailsContext.Provider
      value={{
        isInUpdateCompanyDetailsContext,
        checkedTabs,
        setCheckedTabs,
        selectionsMade,
        setSelectionsMade,
        isStrikeOffSelected,
        setIsStrikeOffSelected,
        createdTransactionsIds,
        setCreatedTransactionsIds,
        handleGoBack,
        handleGoForward,
        transactions,
        addUniqueTransactionId,
        transactionExists,
        fileNameToDelete,
        setFileNameToDelete,
        setRefechTransactionsOnUpdate,
        refechTransactionsOnUpdate,
        selectionErrors,
        setSelectionErrors,
        isConversionSelected,
        setIsConversionSelected,
        fetchTransactions,
      }}
    >
      <div>
        <div className="bg-white container mx-auto px-4 mb-8">
          <div className="">
            <BreadCrumb pages={pages} />
          </div>
        </div>
        <div>
          <Tabs
            tabs={tabs}
            selectedTabIndex={selectedTabIndex}
            setSelectedTabIndex={setSelectedTabIndex}
          />
        </div>

        <div className="pt-4">
          {selectedTab?.id === "applicantDetails" && (
            <ApplicantDetails handleGoForward={handleGoForward} />
          )}
          {selectedTab?.id === "changeType" && (
            <ChangeType
              handleGoBack={handleGoBack}
              updateParentTabs={updateParentTabs}
              removeParentTab={removeParentTab}
            />
          )}
          {selectedTab?.id === "summary" && (
            <Summary
              summarySubTabIndex={summarySubTabIndex}
              setSummarySubTabIndex={setSummarySubTabIndex}
              handleGoBack={handleGoBack}
              handleGoForward={handleGoForward}
            />
          )}
          {selectedTab?.id === "registeredAddress" && (
            <RegisteredAddress
              handleGoBack={handleGoBack}
              handleGoForward={handleGoForward}
            />
          )}
          {selectedTab?.id === "shareInformation" && (
            <ShareInformation
              handleGoBack={handleGoBack}
              handleGoForward={handleGoForward}
            />
          )}
          {selectedTab?.id === "stakeholderDetails" && (
            <StakeholderDetails
              handleGoBack={handleGoBack}
              handleGoForward={handleGoForward}
            />
          )}
          {selectedTab?.id === "beneficialOwners" && (
            <BeneficialOwners
              handleGoBack={handleGoBack}
              handleGoForward={handleGoForward}
            />
          )}
          {selectedTab?.id === "approvals" && (
            <Approvals
              handleGoBack={handleGoBack}
              handleGoForward={handleGoForward}
            />
          )}
          {selectedTab?.id === "brsForms" && (
            <BRSForms
              handleGoBack={handleGoBack}
              handleGoForward={handleGoForward}
            />
          )}
          {selectedTab?.id === "status" && (
            <Status
              submitToLabel={submitToLabel}
              setDisableSubmitBtn={setDisableSubmitBtn}
              agreeToSubmit={agreeToSubmit}
              agreeToTerms={agreeToTerms}
              setAgreeToSubmit={setAgreeToSubmit}
              setAgreeToTerms={setAgreeToTerms}
              handleGoBack={handleGoBack}
            />
          )}
        </div>
      </div>
    </UpdateCompanyDetailsContext.Provider>
  );
};

export default UpdateCompanyDetails;

function transactionEditTabs(
  transactionToEdit,
  setCheckedTabs,
  checkedTabs,
  setSelectionsMade,
  setTabs,
  setSelectedTabIndex,
  setIsStrikeOffSelected,
  setIsConversionSelected,
  company
) {
  useEffect(() => {
    if (transactionToEdit) {
      const isPLC = company?.type === "PLC";

      let newInitialTabs = [
        {
          id: "applicantDetails",
          label: "Applicant Details",
        },
        {
          id: "changeType",
          label: "Change Type",
        },
      ];
      if (
        transactionToEdit.changeType ===
        Constants.TRANSACTION_CHANGE_TYPES.CHANGE_OF_NAME
      ) {
        // handle CHANGE OF NAME
        setCheckedTabs([...checkedTabs, "summary"]);
        setSelectionsMade((prev) => ({
          ...prev,
          ["summary"]: {
            ...prev["summary"],
            ["companyChanges"]: ["changeOfCompanyName"],
          },
        }));
        newInitialTabs.push({
          id: "summary",
          label: "Summary",
        });
      } else if (
        transactionToEdit.changeType ===
        Constants.TRANSACTION_CHANGE_TYPES.CHANGE_OF_FY
      ) {
        // handle CHANGE OF FY
        setCheckedTabs([...checkedTabs, "summary"]);
        setSelectionsMade((prev) => ({
          ...prev,
          ["summary"]: {
            ...prev["summary"],
            ["companyChanges"]: ["accountingReferenceDate"],
          },
        }));
        newInitialTabs.push({
          id: "summary",
          label: "Summary",
        });
      } else if (
        transactionToEdit.changeType ===
        Constants.TRANSACTION_CHANGE_TYPES.STRIKE_OFF
      ) {
        // handle STRIKE OFF
        setCheckedTabs([...checkedTabs, "summary"]);
        setSelectionsMade((prev) => ({
          ...prev,
          ["summary"]: {
            ...prev["summary"],
            ["companyChanges"]: ["strikeOffApplication"],
          },
        }));
        setIsStrikeOffSelected(true);
        newInitialTabs.push({
          id: "summary",
          label: "Summary",
        });
      } else if (
        transactionToEdit.changeType ===
        Constants.TRANSACTION_CHANGE_TYPES.CONVERSION
      ) {
        const conversionType = isPLC
          ? "conversionPLCtoPVT"
          : "conversionPVTtoPLC";

        // handle CONVERSION
        setCheckedTabs([...checkedTabs, "summary"]);
        setSelectionsMade((prev) => ({
          ...prev,
          ["summary"]: {
            ...prev["summary"],
            ["companyChanges"]: [conversionType], // or conversionPLCtoPVT depending on type
          },
        }));
        setIsConversionSelected(true);
        newInitialTabs.push({
          id: "summary",
          label: "Summary",
        });
      } else if (
        transactionToEdit.changeType ===
        Constants.TRANSACTION_CHANGE_TYPES.YYYY_ANNUAL_RETURNS
      ) {
        // handle CONVERSION
        setCheckedTabs([...checkedTabs, "summary"]);
        setSelectionsMade((prev) => ({
          ...prev,
          ["summary"]: {
            ...prev["summary"],
            ["annualCompliance"]: ["annualReturns"], // or conversionPLCtoPVT depending on type
          },
        }));
        newInitialTabs.push({
          id: "summary",
          label: "Summary",
        });
      } else if (
        transactionToEdit.changeType ===
        Constants.TRANSACTION_CHANGE_TYPES.YYYY_FINANCIAL_STATEMENTS
      ) {
        // handle CONVERSION
        setCheckedTabs([...checkedTabs, "summary"]);
        setSelectionsMade((prev) => ({
          ...prev,
          ["summary"]: {
            ...prev["summary"],
            ["annualCompliance"]: ["financialStatements"], // or conversionPLCtoPVT depending on type
          },
        }));
        newInitialTabs.push({
          id: "summary",
          label: "Summary",
        });
      } else if (
        transactionToEdit.changeType ===
        Constants.TRANSACTION_CHANGE_TYPES.CHANGE_OF_ARTICLES
      ) {
        // handle CHANGE OF ARTICLES
        setCheckedTabs([...checkedTabs, "summary"]);
        setSelectionsMade((prev) => ({
          ...prev,
          ["summary"]: {
            ...prev["summary"],
            ["governanceChanges"]: ["amendmentOfArticles"],
          },
        }));
        newInitialTabs.push({
          id: "summary",
          label: "Summary",
        });
      } else if (
        transactionToEdit.changeType ===
        Constants.TRANSACTION_CHANGE_TYPES.NOTICE_TO_CANCEL_CONVERSION
      ) {
        const cancellationType = isPLC
          ? "noticeToCancelConversionPLCtoPVT"
          : "noticeToCancelConversionPVTtoPLC";

        // handle NOTICE TO CANCEL CONVERSION
        setCheckedTabs([...checkedTabs, "summary"]);
        setSelectionsMade((prev) => ({
          ...prev,
          ["summary"]: {
            ...prev["summary"],
            ["companyChanges"]: [cancellationType],
          },
        }));
        newInitialTabs.push({
          id: "summary",
          label: "Summary",
        });
      } else if (
        transactionToEdit.changeType ===
        Constants.TRANSACTION_CHANGE_TYPES.NOTICE_OF_WITHDRAWAL_OF_STRIKE_OFF
      ) {
        // handle CHANGE OF ARTICLES
        setCheckedTabs([...checkedTabs, "summary"]);
        setSelectionsMade((prev) => ({
          ...prev,
          ["summary"]: {
            ...prev["summary"],
            ["companyChanges"]: ["noticeOfWithdrawalOfStrikeOffApplication"],
          },
        }));
        newInitialTabs.push({
          id: "summary",
          label: "Summary",
        });
      } else if (
        transactionToEdit.changeType ===
        Constants.TRANSACTION_CHANGE_TYPES.ADMINISTRATIVE_RESTORATION
      ) {
        // handle CHANGE OF ARTICLES
        setCheckedTabs([...checkedTabs, "summary"]);
        setSelectionsMade((prev) => ({
          ...prev,
          ["summary"]: {
            ...prev["summary"],
            ["companyChanges"]: ["applicationForAdministrativeRestoration"],
          },
        }));
        newInitialTabs.push({
          id: "summary",
          label: "Summary",
        });
      } else if (
        transactionToEdit.changeType ===
        Constants.TRANSACTION_CHANGE_TYPES.CHANGE_OF_REGISTERED_ADDRESS
      ) {
        // handle CHANGE OF REGISTERED ADDRESS
        setCheckedTabs([...checkedTabs, "registeredAddress"]);
        setSelectionsMade((prev) => ({
          ...prev,
          ["registeredAddress"]: {
            ...prev["registeredAddress"],
            ["changeOfAddress"]: ["changeOfRegisteredAddress"],
          },
        }));
        newInitialTabs.push({
          id: "registeredAddress",
          label: "Registered Address",
        });
      } else if (
        transactionToEdit.applicationType ===
        Constants.TRANSACTION_APPLICATION_TYPES.SHARE_CAPITAL
      ) {
        if (
          transactionToEdit.changeType ===
          Constants.TRANSACTION_CHANGE_TYPES.ALLOTMENT
        ) {
          // handle ALLOTMENT
          setCheckedTabs([...checkedTabs, "shareInformation"]);
          setSelectionsMade((prev) => ({
            ...prev,
            ["shareInformation"]: {
              ...prev.shareInformation,
              ["capitalIncrease"]: ["PVTwithOneClass"] || [
                  "unallotedShares",
                ] || ["existingAuth"],
            },
          }));
        } else if (
          transactionToEdit.changeType ===
          Constants.TRANSACTION_CHANGE_TYPES.AUTHORIZATION
        ) {
          // handle AUTHORIZATION
          setCheckedTabs([...checkedTabs, "shareInformation"]);
          setSelectionsMade((prev) => ({
            ...prev,
            ["shareInformation"]: {
              ...prev["shareInformation"],
              ["capitalIncrease"]: ["authorization"],
            },
          }));
        } else if (
          transactionToEdit.changeType ===
          Constants.TRANSACTION_CHANGE_TYPES.AUTHORIZATION_AND_ALLOTMENT
        ) {
          // handle AUTHORIZATION AND ALLOTMENT
          setCheckedTabs([...checkedTabs, "shareInformation"]);
          setSelectionsMade((prev) => ({
            ...prev,
            ["shareInformation"]: {
              ...prev["shareInformation"],
              ["capitalIncrease"]: ["privateWithMoreThanOneClass"], // or another mapping if needed
            },
          }));
        } else if (
          transactionToEdit.changeType ===
          Constants.TRANSACTION_CHANGE_TYPES.SHARE_TRANSFER
        ) {
          // handle SHARE TRANSFER
          setCheckedTabs([...checkedTabs, "shareInformation"]);
          setSelectionsMade((prev) => ({
            ...prev,
            ["shareInformation"]: {
              ...prev["shareInformation"],
              ["shareholdingChanges"]: ["shareTransfer"],
            },
          }));
        } else if (
          transactionToEdit.changeType ===
          Constants.TRANSACTION_CHANGE_TYPES.TRANSMISSION_OF_SHARES
        ) {
          // handle TRANSMISSION OF SHARES
          setCheckedTabs([...checkedTabs, "shareInformation"]);
          setSelectionsMade((prev) => ({
            ...prev,
            ["shareInformation"]: {
              ...prev["shareInformation"],
              ["shareholdingChanges"]: ["transferShares"],
            },
          }));
        } else if (
          transactionToEdit.changeType ===
          Constants.TRANSACTION_CHANGE_TYPES.FORFEITURE_OF_SHARES
        ) {
          // handle FORFEITURE OF SHARES
          setCheckedTabs([...checkedTabs, "shareInformation"]);
          setSelectionsMade((prev) => ({
            ...prev,
            ["shareInformation"]: {
              ...prev["shareInformation"],
              ["shareholdingChanges"]: ["forfeitureOfShares"],
            },
          }));
        } else if (
          transactionToEdit.changeType ===
          Constants.TRANSACTION_CHANGE_TYPES.SURRENDER_OF_SHARES
        ) {
          // handle SURRENDER OF SHARES
          setCheckedTabs([...checkedTabs, "shareInformation"]);
          setSelectionsMade((prev) => ({
            ...prev,
            ["shareInformation"]: {
              ...prev["shareInformation"],
              ["shareholdingChanges"]: ["surrenderOfShares"],
            },
          }));
        } else if (
          transactionToEdit.changeType ===
          Constants.TRANSACTION_CHANGE_TYPES.SHARES_BUYBACK
        ) {
          // handle SHARES BUYBACK
          setCheckedTabs([...checkedTabs, "shareInformation"]);
          setSelectionsMade((prev) => ({
            ...prev,
            ["shareInformation"]: {
              ...prev["shareInformation"],
              ["shareholdingChanges"]: ["sharesBuyback"],
            },
          }));
        } else if (
          transactionToEdit.changeType ===
          Constants.TRANSACTION_CHANGE_TYPES.CANCELATION_OF_TREASURY_SHARES
        ) {
          // handle CANCELATION OF TREASURY SHARES
          setCheckedTabs([...checkedTabs, "shareInformation"]);
          setSelectionsMade((prev) => ({
            ...prev,
            ["shareInformation"]: {
              ...prev["shareInformation"],
              ["shareholdingChanges"]: ["cancelationOfTreasuryShares"],
            },
          }));
        } else if (
          transactionToEdit.changeType ===
          Constants.TRANSACTION_CHANGE_TYPES.SALE_TRANSFER_OF_TREASURY_SHARES
        ) {
          // handle SALE TRANSFER OF TREASURY SHARES
          setCheckedTabs([...checkedTabs, "shareInformation"]);
          setSelectionsMade((prev) => ({
            ...prev,
            ["shareInformation"]: {
              ...prev["shareInformation"],
              ["shareholdingChanges"]: ["sale/TransferOfTreasuryShares"],
            },
          }));
        } else if (
          transactionToEdit.changeType ===
          Constants.TRANSACTION_CHANGE_TYPES.SHARES_REDEMPTION
        ) {
          // handle SHARES REDEMPTION
          setCheckedTabs([...checkedTabs, "shareInformation"]);
          setSelectionsMade((prev) => ({
            ...prev,
            ["shareInformation"]: {
              ...prev["shareInformation"],
              ["shareholdingChanges"]: ["sharesRedemption"],
            },
          }));
        } else if (
          transactionToEdit.changeType ===
          Constants.TRANSACTION_CHANGE_TYPES.TAKEOVER_OFFER
        ) {
          // handle TAKEOVER OFFER
          setCheckedTabs([...checkedTabs, "shareInformation"]);
          setSelectionsMade((prev) => ({
            ...prev,
            ["shareInformation"]: {
              ...prev["shareInformation"],
              ["shareholdingChanges"]: ["takeoverOffer"],
            },
          }));
        } else if (
          transactionToEdit.changeType ===
          Constants.TRANSACTION_CHANGE_TYPES.SHARES_CONSOLIDATION
        ) {
          // handle SHARES CONSOLIDATION
          setCheckedTabs([...checkedTabs, "shareInformation"]);
          setSelectionsMade((prev) => ({
            ...prev,
            ["shareInformation"]: {
              ...prev["shareInformation"],
              ["sharesManagement"]: ["sharesConsolidation"],
            },
          }));
        } else if (
          transactionToEdit.changeType ===
          Constants.TRANSACTION_CHANGE_TYPES.SHARES_SUBDIVISION
        ) {
          // handle SHARES SUBDIVISION
          setCheckedTabs([...checkedTabs, "shareInformation"]);
          setSelectionsMade((prev) => ({
            ...prev,
            ["shareInformation"]: {
              ...prev["shareInformation"],
              ["sharesManagement"]: ["sharesSubdivision"],
            },
          }));
        } else if (
          transactionToEdit.changeType ===
          Constants.TRANSACTION_CHANGE_TYPES.SOLVENCY_STATEMENT
        ) {
          // handle SOLVENCY STATEMENT
          setCheckedTabs([...checkedTabs, "shareInformation"]);
          setSelectionsMade((prev) => ({
            ...prev,
            ["shareInformation"]: {
              ...prev["shareInformation"],
              ["capitalReduction"]: ["solvencyStatement"],
            },
          }));
        } else if (
          transactionToEdit.changeType ===
          Constants.TRANSACTION_CHANGE_TYPES.COURT_ORDER
        ) {
          // handle COURT ORDER
          setCheckedTabs([...checkedTabs, "shareInformation"]);
          setSelectionsMade((prev) => ({
            ...prev,
            ["shareInformation"]: {
              ...prev["shareInformation"],
              ["capitalReduction"]: ["courtOrder"],
            },
          }));
        }

        newInitialTabs.push({
          id: "shareInformation",
          label: "Share Information",
        });
        newInitialTabs.push({
          id: "stakeholderDetails",
          label: "Stakeholder Details",
        });
        if (
          transactionToEdit.changeType !==
            Constants.TRANSACTION_CHANGE_TYPES.SHARES_CONSOLIDATION &&
          transactionToEdit.changeType !==
            Constants.TRANSACTION_CHANGE_TYPES.SHARES_SUBDIVISION
        ) {
          newInitialTabs.push({
            id: "beneficialOwners",
            label: "Beneficial Owners",
          });
        }
      } else if (
        transactionToEdit.changeType ===
        Constants.TRANSACTION_CHANGE_TYPES.APPOINTMENT
      ) {
        // handle APPOINTMENT
        setCheckedTabs([...checkedTabs, "stakeholderDetails"]);
        setSelectionsMade((prev) => ({
          ...prev,
          ["stakeholderDetails"]: {
            ...prev["stakeholderDetails"],
            ["appointment"]: [transactionToEdit.applicationType],
          },
        }));
        newInitialTabs.push({
          id: "stakeholderDetails",
          label: "Stakeholder Details",
        });
      } else if (
        transactionToEdit.changeType ===
        Constants.TRANSACTION_CHANGE_TYPES.CHANGE_OF_PARTICULARS
      ) {
        if (
          transactionToEdit.applicationType ===
          Constants.APPLICANT_CAPACITIES.BENEFICIAL_OWNER
        ) {
          // handle CHANGE OF PARTICULARS
          setCheckedTabs([...checkedTabs, "beneficialOwners"]);
          setSelectionsMade((prev) => ({
            ...prev,
            ["beneficialOwners"]: {
              ...prev["beneficialOwners"],
              ["changesBeneficialOwners"]: [transactionToEdit.applicationType],
            },
          }));
          newInitialTabs.push({
            id: "beneficialOwners",
            label: "Beneficial Owners",
          });
        } else {
          // handle CHANGE OF PARTICULARS
          setCheckedTabs([...checkedTabs, "stakeholderDetails"]);
          setSelectionsMade((prev) => ({
            ...prev,
            ["stakeholderDetails"]: {
              ...prev["stakeholderDetails"],
              ["changeOfParticulars"]: [transactionToEdit.applicationType],
            },
          }));
          newInitialTabs.push({
            id: "stakeholderDetails",
            label: "Stakeholder Details",
          });
        }
      } else if (
        transactionToEdit.changeType ===
        Constants.TRANSACTION_CHANGE_TYPES.CESSATION
      ) {
        // handle CESSATION
        setCheckedTabs([...checkedTabs, "stakeholderDetails"]);
        setSelectionsMade((prev) => ({
          ...prev,
          ["stakeholderDetails"]: {
            ...prev["stakeholderDetails"],
            ["cessation"]: [transactionToEdit.applicationType],
          },
        }));
        newInitialTabs.push({
          id: "stakeholderDetails",
          label: "Stakeholder Details",
        });
      }
      newInitialTabs.push(
        {
          id: "approvals",
          label: "Approvals",
        },
        {
          id: "brsForms",
          label: "BRS Forms",
        },
        {
          id: "status",
          label: "Status",
        }
      );
      setTabs(newInitialTabs);
      setSelectedTabIndex(2);
    }
  }, [transactionToEdit]);
}
